import React, { useEffect, useMemo, useState } from "react";
import { Content } from "@iventis/translations/content/typed-content";
import { Caption, Header4, Label1 } from "@iventis/styles/src/components/texts";
import { Field, FormikProps } from "formik";
import { DataField } from "@iventis/domain-model/model/dataField";
import { BehaviorSubject } from "rxjs";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { checkboxWithLabelCss } from "@iventis/styles/src/atomic-rules";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { media, muiInputFormsCSS, styled } from "@iventis/styles";
import { TimeWithPrepositionSelector } from "@iventis/components";
import { StyledColumnFlex, StyledFieldLabel } from "@iventis/styles/src/components/forms";

import { ScheduleConfig } from "@iventis/domain-model/model/scheduleConfig";
import { ScheduleConfigEventBlockType } from "@iventis/domain-model/model/scheduleConfigEventBlockType";
import { EventBlockSystemDataFieldType } from "@iventis/domain-model/model/eventBlockSystemDataFieldType";
import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { VenueLocation } from "@iventis/domain-model/model/venueLocation";
import { DataFieldValueTypes } from "@iventis/types";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { CheckBoxStyled } from "@iventis/components/src/checkbox";
import { titleFormat, useConstant } from "@iventis/utilities";
import CollapsibleSessionSection from "./session-section";
import SessionScheduleVersionSelector from "./session-schedule-version";
import SessionDayWrapperFormik from "./session-day-formik-wrapped";
import WrapperCustomFormikField from "./formik-components/wrappers/wrapper-custom-formik-field";
import DurationInput from "./formik-components/duration-input";
import { SessionFormValues } from "./session.types";
import { ScheduleVersionNode } from "./schedule.types";
import { DataFieldInput, renderMedalBox, renderOptionWithBox } from "./formik-components/datafield-input";
import { TimeSelectorWrapper } from "./formik-components/wrappers/time-selector-wrapper";
import { SportEventDataFieldNames, getDataField } from "./datafields";
import { dataFieldExists } from "./data-field-helpers";
import { getVenueCodeAndName } from "./sport-event-helpers";
import { hasEventBlockUpdated, mapSessionFormValuesToEventBlock } from "./event-block-helpers";
import { findHierarchyEntitiesByType } from "./hierarchy.helpers";
import { VenueSelector, VenueSelectorButton } from "./venue-selector";
import { includeThirdLevelVenue } from "./schedule-helper-functions";

interface SessionFormProps extends FormikProps<SessionFormValues> {
    scheduleConfig: ScheduleConfig;
    sessionConfig: ScheduleConfigEventBlockType;
    setCanSubmit: (isValid: boolean) => void;
    scheduleVersionName$: BehaviorSubject<string>;
    dataField: DataField;
    dataFields: DataField[];
    scheduleVersions: ScheduleVersionNode[];
    sessionsInSelectedSchedule: Hierarchy<EventBlock>[];
    venues: Hierarchy<VenueLocation>[];
    isScheduleManager: boolean;
    scheduleDaysMap: Map<number, Date>;
    editing: boolean;
    existingSession: EventBlock;
    sessionCodeDataField: DataField;
    timezone: string;
}

const CreateSessionFormComponent: React.FunctionComponent<SessionFormProps> = ({
    scheduleConfig,
    sessionConfig,
    dataField,
    dataFields,
    scheduleVersions,
    venues,
    isScheduleManager,
    scheduleDaysMap,
    setCanSubmit,
    scheduleVersionName$,
    editing,
    existingSession,
    sessionCodeDataField,
    timezone,
    ...formikProps
}) => {
    const { values, isValid, handleSubmit, handleChange, handleBlur, setFieldValue, touched, errors } = formikProps;
    const translate = useIventisTranslate();

    const [dataFieldValue, setDataFieldValue] = useState<DataFieldValueTypes>(null);

    // Venues won't change once you open the session dialog - hence no dependencies
    const venueOptions = useConstant(() => {
        const venueValues = findHierarchyEntitiesByType(venues, sessionConfig.allowedVenueTypes?.[0], (venueLocation: VenueLocation) => venueLocation.type.id);
        if (venueValues.length === 0) {
            return [];
        }
        // Check can be removed when system datafields for venueLocations is implemented
        if (dataFieldExists(venueValues[0].item, SportEventDataFieldNames.VENUE_CODE)) {
            return venueValues.sort((a, b) =>
                // instead use the system data field code
                getVenueCodeAndName(a.item).localeCompare(getVenueCodeAndName(b.item))
            );
        }
        return venueValues;
    });

    // Update form container to enable/disable save button
    useEffect(() => {
        let canSubmit = !editing;
        if (editing) {
            const latestEventBlock = mapSessionFormValuesToEventBlock(values, existingSession, sessionCodeDataField, timezone, [...dataFields, sessionCodeDataField]);
            const hasUpdated = hasEventBlockUpdated(existingSession, latestEventBlock);
            if (hasUpdated) {
                canSubmit = true;
            }
        }
        setCanSubmit(isValid && canSubmit);
    }, [isValid, values]);

    useEffect(() => scheduleVersionName$.next(values.scheduleVersion.name), [values.scheduleVersion.name]);

    /*
        Data field UI assignment
    */
    // System Datafield - Style
    const sessionStyleDataField = useMemo(() => {
        const codeDataField = sessionConfig.dataFields.find((dataField) => dataField.systemDataFieldType === EventBlockSystemDataFieldType.Style);
        return scheduleConfig.dataFields.find(({ id }) => id === codeDataField.dataFieldId);
    }, []);

    // Normal datafields
    const sessionTypeDataField = useMemo(() => getDataField(dataFields, SportEventDataFieldNames.SESSION_TYPE), [dataFields]);
    const ticketedDataField = useMemo(() => getDataField(dataFields, SportEventDataFieldNames.TICKETED), [dataFields]);
    const medalTypeDataField = useMemo(() => getDataField(dataFields, SportEventDataFieldNames.MEDAL_TYPE), [dataFields]);
    const medalCeremonyDataField = useMemo(() => getDataField(dataFields, SportEventDataFieldNames.MEDAL_CEREMONY), [dataFields]);
    const manualLayoutDataFieldIds = [sessionStyleDataField?.id, sessionTypeDataField?.id, ticketedDataField?.id, medalTypeDataField?.id, medalCeremonyDataField?.id];
    const otherDatafields = useMemo(() => dataFields.filter((d) => !manualLayoutDataFieldIds.includes(d.id)), [dataFields]);

    const setDataFieldValues = (dataFieldId: string, value: DataFieldValueTypes) => setFieldValue(`dataFieldValues[${dataFieldId}]`, value);

    return (
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <StyledSessionFormContent>
                <StaticSessionSection>
                    <StaticSectionHeader>
                        <Header4>{translate(Content.schedule.create_session_form.headings.details)}</Header4>
                    </StaticSectionHeader>
                    <SessionScheduleVersionSelector
                        scheduleNodeVersions={scheduleVersions}
                        dataField={dataField}
                        name="scheduleVersion"
                        isScheduleManager={isScheduleManager}
                        dataFieldValue={dataFieldValue}
                        setDataFieldValue={setDataFieldValue}
                        disabled={editing}
                    />
                    <StyledSectionHorizontalInner>
                        <StyledSectionHorizontalInnerHalf>
                            <StyledFieldLabel>{translate(Content.schedule.create_session_form.inputLabels.code)}</StyledFieldLabel>
                            <div className="flex-and-gap">
                                <WrapperCustomFormikField
                                    name="sessionCode.sessionCodePrefix"
                                    value={values.sessionCode.sessionCodePrefix}
                                    onChange={handleChange}
                                    width="25%"
                                    disabled
                                />
                                <WrapperCustomFormikField name="sessionCode.sessionCodeDigit" value={values.sessionCode.sessionCodeDigit} onChange={handleChange} width="25%" />
                            </div>
                        </StyledSectionHorizontalInnerHalf>
                        <StyledSectionHorizontalInnerHalf>
                            {sessionStyleDataField && (
                                <DataFieldInput
                                    label={translate(Content.schedule.create_session_form.inputLabels.style)}
                                    key={sessionStyleDataField.id}
                                    dataField={sessionStyleDataField}
                                    setFieldValue={setDataFieldValues}
                                    handleBlur={handleBlur}
                                    handleChange={handleChange}
                                    touched={touched}
                                    errors={errors}
                                    values={values.dataFieldValues}
                                    renderOption={renderOptionWithBox}
                                    renderAdornment={(value: DataFieldListItem) => renderMedalBox(value?.metaData?.colour || null)}
                                />
                            )}
                        </StyledSectionHorizontalInnerHalf>
                    </StyledSectionHorizontalInner>
                    <StyledSectionHorizontalInner>
                        <WrapperCustomFormikField
                            label={translate(Content.schedule.create_session_form.inputLabels.description)}
                            name="description"
                            value={values.description}
                            onChange={handleChange}
                            rows={3}
                            multiline
                        />
                    </StyledSectionHorizontalInner>
                </StaticSessionSection>
                <CollapsibleSessionSection sectionName={translate(Content.schedule.create_session_form.headings.time)} initialExpanded>
                    <StyledSectionHorizontalInner className="session-day-container">
                        <StyledSectionHorizontalInnerHalf>
                            <SessionDayWrapperFormik
                                label={translate(Content.schedule.create_session_form.inputLabels.day)}
                                name="sessionDay"
                                scheduleDaysMap={scheduleDaysMap}
                                timezone={timezone}
                            />
                        </StyledSectionHorizontalInnerHalf>
                    </StyledSectionHorizontalInner>
                    <StyledSectionHorizontalInner>
                        <StyledSectionHorizontalInnerHalf>
                            <TimeSelectorWrapper
                                value={values.startTime}
                                onChange={(e) => setFieldValue("startTime", e.currentTarget.value)}
                                timeName="startTime"
                                error={errors?.startTime != null}
                                helperText={errors?.startTime}
                            />
                        </StyledSectionHorizontalInnerHalf>
                        <StyledSectionHorizontalInnerHalf>
                            <TimeSelectorWrapper
                                value={values.endTime}
                                onChange={(e) => setFieldValue("endTime", e.currentTarget.value)}
                                timeName="endTime"
                                error={errors?.endTime != null}
                                helperText={errors?.endTime}
                            />
                        </StyledSectionHorizontalInnerHalf>
                    </StyledSectionHorizontalInner>
                    <StyledSectionHorizontalInner>
                        <StyledSectionHorizontalInnerHalf>
                            <StyledColumnFlex className="flex-and-gap" width="56%">
                                <DurationInput value={values.duration} setFieldValue={setFieldValue} startTime={values.startTime} endTime={values.endTime} />
                            </StyledColumnFlex>
                        </StyledSectionHorizontalInnerHalf>
                    </StyledSectionHorizontalInner>
                    <StyledSectionHorizontalInner>
                        <StyledSectionHorizontalInnerHalf>
                            <StyledColumnFlex>
                                {sessionTypeDataField && (
                                    <DataFieldInput
                                        key={sessionTypeDataField.id}
                                        dataField={sessionTypeDataField}
                                        setFieldValue={setDataFieldValues}
                                        handleBlur={handleBlur}
                                        handleChange={handleChange}
                                        touched={touched}
                                        errors={errors}
                                        values={values.dataFieldValues}
                                    />
                                )}
                            </StyledColumnFlex>
                        </StyledSectionHorizontalInnerHalf>
                    </StyledSectionHorizontalInner>
                </CollapsibleSessionSection>
                <CollapsibleSessionSection sectionName={translate(Content.schedule.create_session_form.headings.ticketingAndBroadcasting)} initialExpanded>
                    <StyledSectionHorizontalInner>
                        <StyledColumnFlex width="100%">
                            <StyledFieldLabel>{translate(Content.schedule.create_session_form.inputLabels.zTime)}</StyledFieldLabel>

                            <div className="inner-inner-checkbox">
                                <CheckBoxStyled
                                    styleAdditions={{ padding: "0px" }}
                                    checked={values.zeroTimeDisabled}
                                    onChange={(e) => {
                                        setFieldValue("zeroTimeDisabled", e.currentTarget.checked);
                                    }}
                                />
                                <Label1>{translate(Content.schedule.create_session_form.inputLabels.zeroCheckBoxLabel)}</Label1>
                            </div>

                            <TimeWithPrepositionSelector
                                disabled={values.zeroTimeDisabled}
                                timeSpanValue={values.zeroTime}
                                onChange={(e) => {
                                    setFieldValue("zeroTime", e);
                                }}
                                name="zeroTime"
                                afterPrepositionLabel={translate(Content.timerules.preposition_lables.after, { type: sessionConfig?.name?.toLowerCase() })}
                                beforePrepositionLabel={translate(Content.timerules.preposition_lables.before, { type: sessionConfig?.name?.toLowerCase() })}
                                error={errors.zeroTime != null}
                            />
                        </StyledColumnFlex>
                    </StyledSectionHorizontalInner>
                    {ticketedDataField && (
                        <>
                            <StyledSectionHorizontalInner>
                                <StyledColumnFlex>
                                    <StyledFieldLabel>{translate(Content.schedule.create_session_form.headings.ticketed)}</StyledFieldLabel>

                                    <div className="inner-inner-checkbox">
                                        <CheckBoxStyled
                                            styleAdditions={{ padding: "0px" }}
                                            checked={values.dataFieldValues[`${ticketedDataField.id}`] as boolean}
                                            onChange={(e) => {
                                                setFieldValue(`dataFieldValues[${ticketedDataField.id}]`, e.currentTarget.checked);
                                            }}
                                        />
                                        <Label1>{translate(Content.schedule.create_session_form.inputLabels.ticketed)}</Label1>
                                    </div>

                                    <Caption color="subdued">{translate(Content.schedule.create_session_form.ticketedDescription)}</Caption>
                                </StyledColumnFlex>
                            </StyledSectionHorizontalInner>
                        </>
                    )}
                </CollapsibleSessionSection>
                <CollapsibleSessionSection sectionName={translate(Content.schedule.create_session_form.headings.venue)} initialExpanded>
                    <StyledSectionHorizontalInner>
                        <VenueSelectorButton
                            buttonStyle={{ color: "inherit", fontWeight: "normal", borderColor: "rgb(117, 117, 117, 0.5)", ":hover": { borderColor: "inherit" } }}
                            noneSelectedText={translate(Content.common.buttons.select)}
                            flattenedVenueLocations={venueOptions}
                            selectedVenueLocations={values.venueLocation == null ? [] : [values.venueLocation]}
                        >
                            <VenueSelector
                                configIncludesClusters={includeThirdLevelVenue(scheduleConfig?.venueLocationTypes)}
                                isLoadingEventBlocks={false}
                                selectedVenueLocations={values.venueLocation == null ? [] : [values.venueLocation]}
                                singleSelectionOnly
                                updateSelection={([value]) => setFieldValue("venueLocation", value)}
                                venueLocations={venues}
                            />
                        </VenueSelectorButton>
                    </StyledSectionHorizontalInner>
                </CollapsibleSessionSection>
                {(medalTypeDataField || medalCeremonyDataField) && (
                    <CollapsibleSessionSection sectionName={translate(Content.schedule2.session_form.medal_header)} initialExpanded>
                        <StyledSectionHorizontalInner>
                            {medalTypeDataField && (
                                <StyledSectionHorizontalInnerHalf>
                                    <DataFieldInput
                                        label={translate(Content.schedule2.data_fields.medal_type)}
                                        key={medalTypeDataField.id}
                                        dataField={medalTypeDataField}
                                        setFieldValue={setDataFieldValues}
                                        handleBlur={handleBlur}
                                        handleChange={handleChange}
                                        touched={touched}
                                        errors={errors}
                                        values={values.dataFieldValues}
                                    />
                                </StyledSectionHorizontalInnerHalf>
                            )}
                            {medalCeremonyDataField && (
                                <StyledSectionHorizontalInnerHalf>
                                    <StyledFieldLabel>{translate(Content.schedule2.session_form.medal_ceremony)}</StyledFieldLabel>
                                    <StyledColumnFlex width="15%">
                                        <div className="inner-inner-checkbox">
                                            <CheckBoxStyled
                                                styleAdditions={{ padding: "0px" }}
                                                checked={values.dataFieldValues[`${medalCeremonyDataField.id}`] as boolean}
                                                onChange={(e) => {
                                                    setFieldValue(`dataFieldValues[${medalCeremonyDataField.id}]`, e.currentTarget.checked);
                                                }}
                                            />
                                            <Label1>{translate(Content.schedule2.session_form.includes_medal_ceremony)}</Label1>
                                        </div>
                                    </StyledColumnFlex>
                                </StyledSectionHorizontalInnerHalf>
                            )}
                        </StyledSectionHorizontalInner>
                    </CollapsibleSessionSection>
                )}

                {otherDatafields.map((dataField) => (
                    <CollapsibleSessionSection sectionName={titleFormat(dataField.name)} initialExpanded key={dataField.id}>
                        <StyledSectionHorizontalInner>
                            <DataFieldInput
                                key={dataField.id}
                                dataField={dataField}
                                setFieldValue={setDataFieldValues}
                                handleBlur={handleBlur}
                                handleChange={handleChange}
                                touched={touched}
                                errors={errors}
                                values={values.dataFieldValues}
                            />
                        </StyledSectionHorizontalInner>
                    </CollapsibleSessionSection>
                ))}
            </StyledSessionFormContent>
        </form>
    );
};

const StyledSessionFormContent = styled.div`
    padding: 1.3rem;
    overflow-y: auto;
    overflow-x: hidden;
    scroll-behavior: smooth;
    ${muiInputFormsCSS}

    ${media.extraSmall} {
        .session-day-container {
            gap: 0;
        }
    }
`;

const StaticSessionSection = styled.div`
    display: flex;
    flex-direction: column;
    padding-bottom: 2rem;
    gap: 1.3rem;
    border-bottom: 1px solid #605b6033;
    ${media.extraSmall} {
        gap: 0.8rem;
        padding-bottom: 1rem;
    }
`;
export const StyledSectionHorizontalInner = styled.div`
    display: flex;
    column-gap: 8%;
    justify-content: space-between;
    ${media.extraSmall} {
        flex-direction: column;
        gap: 1rem;
    }

    .MuiInputBase-root {
        padding: 0;
    }
    .inner-inner-checkbox {
        ${checkboxWithLabelCss};
        padding-top: 5px;
        padding-bottom: 15px;
    }
`;
export const StyledSectionHorizontalInnerHalf = styled.div`
    width: 46%;
    display: flex;
    flex-direction: column;
    align-content: flex-end;
    ${media.extraSmall} {
        width: 100%;
    }
    .flex-and-gap {
        display: flex;
        gap: 0.2rem;
        justify-content: flex-start;
    }
    .indent-inner-inner-checkbox {
        display: flex;
        gap: 0.5rem;
        justify-content: flex-start;
        align-items: center;
        padding-left: 0.5rem;
        padding-top: 15px;
    }
    .inner-inner-checkbox {
        display: flex;
        gap: 0.5rem;
        justify-content: flex-start;
        align-items: center;
        padding-top: 5px;
        padding-bottom: 15px;
    }
`;
export const StyledCheckedBoxField = styled(Field)`
    height: 16px;
    width: 16px;
    align-self: center;
    min-width: 12px;
    min-height: 12px;
`;

const StaticSectionHeader = styled.div`
    padding-bottom: 0.3rem;
`;

export default CreateSessionFormComponent;
